/*
 * Copyright (C) 2018 BarD Software s.r.o
 */
import {Vue, Component} from 'vue-facing-decorator';
import {Watch} from 'vue-facing-decorator';
import TeamInfo from './TeamInfo.vue';
import {ProjectDto, VersionDto} from '../Teams';
import {handleXhrError, SelectionModel} from '../utils/Ui';
import {ProjectVersions} from '../ProjectRoutes';
import FadingElement from '../utils/FadingElement';

const HISTORY_BATCH_SIZE = 10;
export const id: string = '#vue-project-history';

const PROJECT_DTO_STUB = { refid: '', name: 'initial', lock: undefined, creationTs: 0 };

@Component
export default class ProjectHistory extends Vue implements ProjectHistoryMethods {
    private project: ProjectDto = PROJECT_DTO_STUB;
    private versions: MutableList<VersionDto> = [];
    private currentVersion?: VersionDto;
    private versionSelectionModel: SelectionModel<VersionDto> = new SelectionModel(
        '#project-history-list',
        '.list-group-item',
        'data-version',
        (a: VersionDto, b: VersionDto) => a.number === b.number) ;
    private cachedVersions: List<VersionDto> = [];

    private afterUpdates: MutableList<JQueryDeferred<any>> = [];
    private readonly glasspane = new FadingElement('#team-info .glasspane');

    public projectRefid(): string {
        return this.project.refid;
    }

    public updated() {
        this.$nextTick(() => {
            this.afterUpdates.map((deferred) => deferred.resolve());
            this.afterUpdates = [];
        });
    }

    // currently this method call only when history is growing
    public updateHistory(project: ProjectDto) {
        if (project.refid === this.projectRefid()) {
            this.updateCurrentHistory();
        } else {
            this.reloadHistory(project);
        }
    }

    public closeFullHistory() {
        this.versions = this.versions.slice(0, HISTORY_BATCH_SIZE); // because project history can be big
        this.cachedVersions = this.versions.slice(HISTORY_BATCH_SIZE, 2 * HISTORY_BATCH_SIZE);
        const teamInfo = this.$parent;
        if (teamInfo instanceof TeamInfo) {
            (teamInfo as TeamInfo).showTeamProjects();
        }
    }

    public showMoreVersionsButton(): boolean {
        return this.cachedVersions.length > 0;
    }

    public loadMoreVersions() {
        this.versions = this.versions.concat(this.cachedVersions);
        this.cachedVersions = [];
        this.prefetchNextHistoryWindow();
    }

    public toggleGlasspane(show: boolean) {
        this.glasspane.toggle(!show);
    }

    @Watch('versions')
    private watchVersions() {
        const id2model = (versionId) => this.versions.find((dto) => dto.number.toString() === versionId);
        const deferred = this.versionSelectionModel.init(id2model, (version) => this.currentVersion = version);
        this.afterUpdates.push(deferred);
    }

    private updateCurrentHistory() {
        ProjectVersions.invoke({
            projectRefid: this.projectRefid(),
        }).then((versions: List<VersionDto>) => {
            const idx = versions.findIndex((v) => v.number === this.versions[0].number);
            if (idx !== -1) {
                this.versions = versions.slice(0, idx).concat(this.versions);
            } else {
                this.versions = versions.slice();
            }
        }).then(() => {
            this.prefetchNextHistoryWindow();
        }).fail((jqXhr) => handleXhrError(jqXhr, 'can\'t load history'));
    }

    private prefetchNextHistoryWindow(): JQueryPromise<any> {
        return ProjectVersions.invoke({
            projectRefid: this.projectRefid(),
            startVersion: this.versions[this.versions.length - 1].number,
        }).then((versions: List<VersionDto>) => {
            this.cachedVersions = versions.slice(1);
        }).fail(() => console.debug('can\'t prefetch history'));
    }

    private reloadHistory(project: ProjectDto) {
        ProjectVersions.invoke({
            projectRefid: project.refid,
        }).then((versions: List<VersionDto>) => {
            this.project = project;
            this.versions = versions.slice();
        }).then(() => {
            this.prefetchNextHistoryWindow();
        }).fail((jqXhr) => handleXhrError(jqXhr, 'can\'t load history'));
    }
}

export interface ProjectHistoryMethods {
    updateHistory(project: ProjectDto);
    projectRefid(): string;
}
